<script setup lang="ts">
import { useIsFetching } from '@tanstack/vue-query'
import type { StudioViews } from "~/types/studio";
/**
 * 216px sidebar · 44px topbar on a desktop. Below lg the sidebar becomes a drawer
 * opened from the topbar, and the seven destinations collapse into a bottom tab bar:
 * the four a phone needs, plus More for the rest.
 */
import SidebarItem from '~/components/layout/SidebarItem.vue'
import NotificationsBell from '~/components/layout/NotificationsBell.vue'
import ActivityIndicator from '~/components/layout/ActivityIndicator.vue'
import MobileTabBar from '~/components/layout/MobileTabBar.vue'
import SidebarQueue from '~/components/layout/SidebarQueue.vue'
import ContextSwitcher from '~/components/layout/ContextSwitcher.vue'
import UserAvatarMenu from '~/components/layout/UserAvatarMenu.vue'
import Icon from '~/components/ui/Icon.vue'
const fetching = useIsFetching({ queryKey: ['studio'] })

const session = useSession()
watch(session, (value) => {
  if (value.isPending) return
  if (!value.data) void navigateTo('/login')
  else if (!value.data.session.activeOrganizationId) void navigateTo('/onboarding')
}, { immediate: true })
const readError = useState<string | null>('studio-read-error', () => null)
const route = useRoute()
const entityCrumb = useEntityCrumbName()
const { data: nav } = useStudioData<StudioViews["/api/nav"]>('/api/nav')
const active = (to: string) => route.path === to || route.path.startsWith(to + '/')

const actions = useStudioActions()
const { switchProject } = useProjectContext()
async function switchOrganization(id: string) {
  if (!nav.value?.orgs.some(org => org.id === id)) return
  try {
    await apiFetch('/me/active-organization', {method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({organizationId:id})})
    localStorage.removeItem('scalty.current-project-id'); window.location.assign('/overview')
  } catch (error) { readError.value = error instanceof Error ? error.message : 'Organisation switch failed' }
}
function orgAction(label: string) { if(label === 'Organisation settings') void navigateTo('/org/current'); else actions.open('organization') }
function projectAction(label: string) { if(label === 'New project') actions.open('project'); else void navigateTo('/org/current/projects') }
function onKey(event: KeyboardEvent) { if((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); actions.open('search') } }
onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))
const drawer = ref(false)
watch(() => route.path, () => { drawer.value = false; readError.value = null })

/* The notification feed is read here, in the shell, not on a page: the layout outlives every
   navigation, so the bell keeps its place in the feed and its read position across the whole
   portal instead of starting again on each page (W8-UI-05 T1). */
const { todayRows, earlierRows, hasUnread, busy: bellBusy, canLoadMore: bellCanLoadMore, initialError: bellError, markAllRead, loadMore: loadMoreNotifications } = useNotifications()
</script>

<template>
  <div class="flex h-screen bg-background text-foreground">
    <!-- Drawer scrim: only below lg, only while open. -->
    <button v-if="drawer" type="button" class="fixed inset-0 z-30 bg-background/70 lg:hidden" aria-label="Close menu" @click="drawer = false" />

    <aside
class="z-40 flex w-[var(--shell-sidebar-width)] shrink-0 flex-col border-r border-sidebar-border bg-sidebar max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:transition-transform"
           :class="drawer ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-full'">
      <div class="flex h-[var(--shell-topbar-height)] shrink-0 items-center gap-2 border-b border-sidebar-border px-3">
        <span class="grid size-5 place-items-center rounded-[var(--radius-control)] bg-gradient-primary text-[length:var(--type-meta)] font-bold text-primary-foreground">S</span>
        <span class="text-[length:var(--type-control)] font-bold tracking-[-0.01em]">Scalty</span>
        <span class="num rounded-[var(--radius-label)] border border-border px-1 py-0.5 text-[length:var(--type-badge)] text-muted-foreground">AI STUDIO</span>
        <button type="button" class="ml-auto text-muted-foreground hover:text-foreground lg:hidden" aria-label="Close menu" @click="drawer = false">
          <Icon name="x" :size="13" />
        </button>
      </div>

      <nav class="flex min-h-0 flex-col gap-px overflow-y-auto p-1.5">
        <div class="flex h-[var(--toolbar-control-height)] items-center px-2 text-[length:var(--type-meta)] font-semibold text-sidebar-foreground/45">Project</div>
        <SidebarItem label="Overview" to="/overview" :active="active('/overview')"><template #icon><Icon name="overview" :size="15" /></template></SidebarItem>
        <SidebarItem label="Datasets" to="/datasets" :active="active('/datasets')" :count="nav?.datasets?.count" :tone="nav?.datasets?.tone"><template #icon><Icon name="datasets" :size="15" /></template></SidebarItem>
        <SidebarItem label="Memory" to="/memory" :active="active('/memory')"><template #icon><Icon name="memory" :size="15" /></template></SidebarItem>
        <SidebarItem label="Models" to="/models" :active="active('/models')" :count="nav?.models.count" :tone="nav?.models.tone"><template #icon><Icon name="models" :size="15" /></template></SidebarItem>
        <SidebarItem label="Deploy" to="/deploy" :active="active('/deploy')" :count="nav?.deploy?.count" :tone="nav?.deploy?.tone"><template #icon><Icon name="deploy" :size="15" /></template></SidebarItem>
        <SidebarItem label="Playground" to="/playground" :active="active('/playground')"><template #icon><Icon name="playground" :size="15" /></template></SidebarItem>
        <SidebarItem label="Settings" to="/settings" :active="active('/settings')"><template #icon><Icon name="settings" :size="15" /></template></SidebarItem>
      </nav>

      <span class="mt-auto" />
      <SidebarQueue :items="nav?.queue ?? []" />
      <UserAvatarMenu />
    </aside>

    <div class="flex min-w-0 flex-1 flex-col">
      <header class="flex h-[var(--shell-topbar-height)] shrink-0 items-center gap-2.5 border-b border-border bg-card/60 px-3">
        <button
type="button" class="grid size-7 shrink-0 place-items-center rounded-[var(--radius-control)] text-muted-foreground hover:bg-muted lg:hidden"
                aria-label="Open menu" @click="drawer = true">
          <Icon name="list" :size="15" />
        </button>
        <!-- Organisation → project → where you are. Both scopes are dropdowns, as in the source. -->
        <div class="flex min-w-0 items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
          <ContextSwitcher
class="hidden md:block" label="Organisations" :items="nav?.orgs ?? []" :actions="[{ label: 'Organisation settings', icon: 'settings', primary: true }, { label: 'New organisation', icon: 'plus' }]" @select="switchOrganization($event.id)"
                           @action="orgAction($event.label)" />
          <span class="hidden md:inline">/</span>
          <ContextSwitcher
label="Projects" mark="folder" :items="nav?.projects ?? []" :actions="[{ label: 'New project', icon: 'plus' }, { label: 'Manage projects', icon: 'settings' }]" @select="switchProject($event.id); navigateTo('/overview')"
                           @action="projectAction($event.label)" />
          <span>/</span>
          <template v-if="route.meta.crumbParent">
            <NuxtLink :to="route.meta.crumbParent.to" class="truncate text-muted-foreground hover:text-foreground">{{ route.meta.crumbParent.label }}</NuxtLink>
            <span class="text-muted-foreground">/</span>
          </template>
          <!-- An entity page names its entity; every other page shows its own word. -->
          <span class="truncate px-1 font-semibold text-foreground">{{ entityCrumb || route.meta.crumb || 'Overview' }}</span>
        </div>
        <span class="flex-1" />
        <button type="button" class="hidden h-7 w-[var(--measure-15p7143rem)] shrink-0 items-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] border border-border bg-muted/50 px-2 text-xs text-muted-foreground md:flex" @click="actions.open('search')">
          <Icon name="search" :size="13" /><span class="flex-1 text-left">Search or jump to…</span>
          <kbd class="num rounded-[var(--radius-label)] border border-border px-1 py-0.5 text-[length:var(--type-fine)]">⌘K</kbd>
        </button>
        <button type="button" class="grid size-7 shrink-0 place-items-center rounded-[var(--radius-control)] text-muted-foreground hover:bg-muted md:hidden" aria-label="Search" @click="actions.open('search')">
          <Icon name="search" :size="15" />
        </button>
        <ActivityIndicator :running="nav?.running ?? 0" />
        <NotificationsBell
          :today="todayRows" :earlier="earlierRows" :unread="hasUnread" :busy="bellBusy" :can-load-more="bellCanLoadMore"
          :empty-text="bellError ? bellError.message : undefined"
          @open="navigateTo($event.to)" @mark-all="markAllRead()" @load-more="loadMoreNotifications()" />
        <button type="button" class="hidden h-7 items-center gap-1.5 rounded-[var(--radius-control)] border border-border px-2.5 text-xs font-medium hover:bg-muted md:inline-flex" @click="navigateTo('/api-reference')"><Icon name="code" :size="13" />API</button>
      </header>

      <main :aria-busy="fetching > 0" class="scrollbar-none min-h-0 flex-1 overflow-auto px-5 py-4 max-lg:px-3 max-lg:pb-16">
        <div v-if="readError" role="alert" class="mb-3 rounded-lg border border-destructive/30 bg-card px-3 py-2 text-xs text-destructive">{{ readError }} <button type="button" class="ml-2 underline" @click="readError = null">Dismiss</button></div>
        <slot />
      </main>

      <MobileTabBar :nav="nav" :active="active" @more="drawer = true" />
    </div>
  </div>
</template>
